import React, { useState } from 'react';
import AppHeader from '../../../components/AppHeader.tsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReceipt,
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faDownload,
  faCalendar,
  faEuroSign,
  faTag,
  faCreditCard,
  faCashRegister,
} from '@fortawesome/free-solid-svg-icons';

const ExpensesPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('month');

  const breadcrumb = [{ label: 'Financování' }, { label: 'Výdaje' }];

  const categories = [
    { id: 'salaries', name: 'Platy zaměstnanců', color: 'bg-blue-100 text-blue-800' },
    { id: 'materials', name: 'Materiál a produkty', color: 'bg-green-100 text-green-800' },
    { id: 'rent', name: 'Pronájem', color: 'bg-purple-100 text-purple-800' },
    { id: 'utilities', name: 'Energie a služby', color: 'bg-yellow-100 text-yellow-800' },
    { id: 'marketing', name: 'Marketing', color: 'bg-pink-100 text-pink-800' },
    { id: 'equipment', name: 'Vybavení', color: 'bg-indigo-100 text-indigo-800' },
    { id: 'other', name: 'Ostatní', color: 'bg-gray-100 text-gray-800' },
  ];

  const expenses = [
    {
      id: '1',
      description: 'Nákup šampónů a kondicionérů',
      category: 'materials',
      amount: 2500,
      date: '2024-11-15',
      paymentMethod: 'Karta',
      vendor: 'Kosmetika Pro',
      receipt: true,
      notes: 'Měsíční dodávka produktů',
    },
    {
      id: '2',
      description: 'Plat za energie - listopad',
      category: 'utilities',
      amount: 1800,
      date: '2024-11-14',
      paymentMethod: 'Převod',
      vendor: 'ČEZ',
      receipt: true,
      notes: 'Elektřina a plyn',
    },
    {
      id: '3',
      description: 'Nákup nůžek a hřebenů',
      category: 'equipment',
      amount: 3200,
      date: '2024-11-12',
      paymentMethod: 'Karta',
      vendor: 'Profesionální nářadí',
      receipt: true,
      notes: 'Nové vybavení pro kadeřníky',
    },
    {
      id: '4',
      description: 'Facebook reklama',
      category: 'marketing',
      amount: 1200,
      date: '2024-11-10',
      paymentMethod: 'Karta',
      vendor: 'Facebook',
      receipt: false,
      notes: 'Reklama na nové služby',
    },
    {
      id: '5',
      description: 'Pronájem prostor - listopad',
      category: 'rent',
      amount: 8500,
      date: '2024-11-01',
      paymentMethod: 'Převod',
      vendor: 'Vlastník budovy',
      receipt: true,
      notes: 'Měsíční nájemné',
    },
  ];

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.description.toLowerCase().includes(searchTerm.toLowerCase()) || expense.vendor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryInfo = (categoryId: string) => {
    return categories.find(cat => cat.id === categoryId) || categories[categories.length - 1];
  };

  const getPaymentIcon = (method: string) => {
    switch (method) {
      case 'Karta':
        return faCreditCard;
      case 'Hotovost':
        return faCashRegister;
      default:
        return faReceipt;
    }
  };

  const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);

  const expensesByCategory = categories
    .map(category => {
      const categoryExpenses = expenses.filter(expense => expense.category === category.id);
      const total = categoryExpenses.reduce((sum, expense) => sum + expense.amount, 0);
      return {
        ...category,
        total,
        count: categoryExpenses.length,
      };
    })
    .filter(cat => cat.total > 0);

  return (
    <div className="h-full flex flex-col">
      <AppHeader title="Výdaje" breadcrumb={breadcrumb} />

      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="p-6 space-y-6">
          {/* Header */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Výdaje</h1>
                <p className="text-gray-600 mt-1">Spravujte a sledujte všechny výdaje</p>
              </div>
              <div className="flex items-center space-x-3">
                <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Nový výdaj
                </button>
                <button className="border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                  <FontAwesomeIcon icon={faDownload} className="mr-2" />
                  Export
                </button>
              </div>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-red-50 rounded-lg">
                  <FontAwesomeIcon icon={faEuroSign} className="text-xl text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Celkové výdaje</p>
                  <p className="text-2xl font-bold text-gray-900">{totalExpenses.toLocaleString()} Kč</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <FontAwesomeIcon icon={faReceipt} className="text-xl text-blue-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Počet výdajů</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredExpenses.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-50 rounded-lg">
                  <FontAwesomeIcon icon={faTag} className="text-xl text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Kategorie</p>
                  <p className="text-2xl font-bold text-gray-900">{expensesByCategory.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <FontAwesomeIcon icon={faCalendar} className="text-xl text-purple-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Průměrný výdaj</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {filteredExpenses.length > 0 ? Math.round(totalExpenses / filteredExpenses.length).toLocaleString() : 0} Kč
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Search and Filters */}
          <section className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex items-center space-x-4">
              <div className="flex-1 relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Hledat výdaje..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
                />
              </div>
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="all">Všechny kategorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select
                value={filterPeriod}
                onChange={e => setFilterPeriod(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500"
              >
                <option value="week">Tento týden</option>
                <option value="month">Tento měsíc</option>
                <option value="quarter">Toto čtvrtletí</option>
                <option value="year">Tento rok</option>
              </select>
              <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                <FontAwesomeIcon icon={faFilter} className="w-4 h-4 mr-2" />
                Více filtrů
              </button>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Expenses List */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Seznam výdajů</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Popis</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kategorie</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Datum</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Částka</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredExpenses.map(expense => {
                      const categoryInfo = getCategoryInfo(expense.category);
                      return (
                        <tr key={expense.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{expense.description}</p>
                              <p className="text-sm text-gray-500">{expense.vendor}</p>
                              {expense.notes && <p className="text-xs text-gray-400 mt-1">{expense.notes}</p>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${categoryInfo.color}`}>{categoryInfo.name}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(expense.date).toLocaleDateString('cs-CZ')}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <FontAwesomeIcon icon={getPaymentIcon(expense.paymentMethod)} className="w-4 h-4 mr-2 text-gray-400" />
                              <span className="text-sm font-medium text-red-600">-{expense.amount.toLocaleString()} Kč</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button className="text-blue-600 hover:text-blue-900 p-1" title="Zobrazit">
                                <FontAwesomeIcon icon={faReceipt} className="w-4 h-4" />
                              </button>
                              <button className="text-gray-600 hover:text-gray-900 p-1" title="Upravit">
                                <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
                              </button>
                              <button className="text-red-600 hover:text-red-900 p-1" title="Smazat">
                                <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Výdaje podle kategorií</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {expensesByCategory.map(category => (
                    <div key={category.id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-900">{category.name}</span>
                        <span className="text-sm text-gray-600">{category.total.toLocaleString()} Kč</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-red-600 h-2 rounded-full transition-all duration-300" style={{ width: `${(category.total / totalExpenses) * 100}%` }}></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{category.count} výdajů</span>
                        <span>{Math.round((category.total / totalExpenses) * 100)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpensesPage;
